import styled from 'styled-components';
import keyBy from 'lodash/keyBy';
import { ComponentType, useMemo } from 'react';
import { Filter24 } from 'ui/icons/aurora/24px/Filter24';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { Exit16, Label16, EmailLine16, SvgIconProps } from 'ui/icons';
import { TagGroup } from 'ui/tags/TagGroup';
import { borderRadius, colors } from 'ui/theme';
import { TagOption } from 'ui/tags/types';

export const DASHBOARD_FILTER_TRIGGER_ID = 'dashboard-filter-trigger';
export const DASHBOARD_FILTER_CLEAR_FILTERS_ID = 'dashboard-filter-clear-filters';
export const DASHBOARD_FILTER_TAG_GROUP_ID = 'dashboard-filter-tag-group';

const getTagValue = (label: string, tags: TagOption[], PrefixIcon: ComponentType<SvgIconProps>) => {
  return tags.length > 0
    ? {
        maxWidth: 250,
        text: tags.join(', '),
        prefix: (
          <>
            <PrefixIcon isColorInherited /> {`${label}:`}
          </>
        ),
      }
    : null;
};

const Container = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  align-content: center;
  padding: 0;
  width: fit-content;
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.borderPrimarySubtle};
  background: ${colors.backgroundSecondary};
`;

export const DashboardFilterTagGroup = ({ openFiltersModal }: { openFiltersModal: () => void }) => {
  const {
    dateRangePreset,
    labelIds = [],
    messageTypeIds = [],
    projectLabelsQuery: { results: labelsData },
    messageTypesQuery: { messageTypes },
    clearFilters,
    trackFilters,
  } = useMissionControlContext();

  const labelsMappers = useMemo(() => {
    return keyBy(labelsData, 'id');
  }, [labelsData]);

  const messageTypesMappers = useMemo(() => {
    return keyBy(messageTypes, 'id');
  }, [messageTypes]);

  const tagValues = useMemo(() => {
    const labelNames = labelIds.map((id) => labelsMappers[id]?.name).filter(Boolean);
    const messageTypeNames = messageTypeIds
      .map((id) => messageTypesMappers[id]?.name)
      .filter(Boolean);

    const labelsTag = getTagValue('Labels', labelNames, Label16);
    const messageTypeTag = getTagValue('Message types', messageTypeNames, EmailLine16);

    return [labelsTag, messageTypeTag].filter((v) => !!v);
  }, [labelIds, messageTypeIds, labelsMappers, messageTypesMappers]);

  return (
    <Container>
      <IconButton
        onClick={openFiltersModal}
        variant={BUTTON_VARIANTS.GHOST}
        data-test={DASHBOARD_FILTER_TRIGGER_ID}
      >
        <Filter24 />
      </IconButton>

      <TagGroup
        maxTags={2}
        tagSize="large"
        value={tagValues}
        data-test={DASHBOARD_FILTER_TAG_GROUP_ID}
      />

      <IconButton
        onClick={() => {
          clearFilters();
          trackFilters({
            datePreset: dateRangePreset,
            campaignLabelIds: [],
            messageTypeIds: [],
          });
        }}
        size={BUTTON_SIZES.SMALL}
        variant={BUTTON_VARIANTS.GHOST}
        data-test={DASHBOARD_FILTER_CLEAR_FILTERS_ID}
      >
        <Exit16 />
      </IconButton>
    </Container>
  );
};
